import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Palette } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "sav", label: "SAV", desc: "Couleur des SAV dans les calendriers" },
  { key: "atelier", label: "Atelier", desc: "Heures atelier (entretien, préparation matériel)" },
  { key: "fournisseur", label: "Fournisseur", desc: "Déplacement / réception fournisseur" },
  { key: "absence_cp", label: "Congés payés (CP)", desc: "Absences de type CP" },
  { key: "absence_rtt_salarie", label: "RTT salarié" },
  { key: "absence_rtt_entreprise", label: "RTT entreprise" },
  { key: "absence_maladie", label: "Arrêt maladie" },
  { key: "absence_intemperies", label: "Intempéries" },
  { key: "absence_justifiee", label: "Absence justifiée" },
  { key: "absence_injustifiee", label: "Absence injustifiée" },
];

const CalendarColorsSection = () => {
  const [colors, setColors] = useState({});
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.getCalendarColors();
        setColors(data.colors || {});
        setDefaults(data.defaults || {});
      } catch {
        toast.error("Impossible de charger la palette");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      const { data } = await api.updateCalendarColors(colors);
      setColors(data.colors);
      toast.success("Palette enregistrée");
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const resetField = (k) => setColors((c) => ({ ...c, [k]: defaults[k] }));

  return (
    <Card className="mt-8" data-testid="calendar-colors-section">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Palette className="h-5 w-5 text-indigo-600" /> Couleurs des calendriers
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Personnalise les couleurs des bulles SAV, atelier, fournisseurs et absences
          dans le planning et le suivi des chantiers. Les commandes utilisent la couleur
          du <strong>type de commande</strong> (configurée ci-dessus).
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-3" data-testid={`color-row-${f.key}`}>
                  <input
                    type="color"
                    value={colors[f.key] || defaults[f.key] || "#cccccc"}
                    onChange={(e) => setColors({ ...colors, [f.key]: e.target.value })}
                    className="h-9 w-12 rounded border cursor-pointer"
                    data-testid={`color-picker-${f.key}`}
                  />
                  <Input
                    type="text"
                    value={colors[f.key] || defaults[f.key] || ""}
                    onChange={(e) => setColors({ ...colors, [f.key]: e.target.value })}
                    className="w-28 font-mono text-xs h-9"
                  />
                  <div className="flex-1">
                    <Label className="text-sm">{f.label}</Label>
                    {f.desc && <p className="text-xs text-muted-foreground">{f.desc}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => resetField(f.key)}
                    className="text-[10px] text-muted-foreground hover:text-indigo-700"
                  >
                    défaut
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="save-calendar-colors">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer la palette"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CalendarColorsSection;
